import { hostBridge } from '@/utils/hostBridge'
import { FlutterBridge } from '@/utils/flutterBridge'
import { bridgeCapabilities } from '@/utils/bridgeCapabilities'
import { showToast } from 'vant'
/**
 * App Bridge - H5 与 Flutter App 交互工具类
 * 
 * 功能说明：
 * 1. 检测是否在 App 环境中运行
 * 2. 获取 App 用户登录状态
 * 3. 调用 App 的 Toast 提示
 * 4. 调用 App 的原生功能
 * 
 * 使用示例：
 * ```typescript
 * import { appBridge } from '~/utils/appBridge'
 * 
 * // 检查是否在 App 中
 * if (appBridge.isInApp()) {
 *   // 获取 App 登录信息
 *   const loginInfo = await appBridge.getAppLogin()
 *   if (loginInfo.isLogin) {
 *     console.log('用户已登录:', loginInfo)
 *   } else {
 *     // 跳转到 App 登录页
 *     appBridge.goToAppLogin()
 *   }
 * }
 * ```
 */

/**
 * App 登录信息接口
 */
export interface AppLoginInfo {
    isLogin: boolean       // 是否已登录
    token?: string         // 用户 token
    userId?: string        // 用户 ID
    userName?: string      // 用户名
    avatar?: string        // 用户头像
    mobile?: string        // 手机号
    [key: string]: any     // 其他扩展字段
}

/**
 * App Bridge 类
 */
class AppBridge {
    /**
     * 检测是否在 App 环境中
     * @returns {boolean} 是否在 App 中
     */
    isInApp(): boolean {
        if (typeof window === 'undefined') return false
        // 主判断：URL 参数 from=app
        const params = new URLSearchParams(window.location.search)
        if (params.get('from') === 'app') return true
        // 备用判断：window.AppInterface 注入（兼容旧逻辑）
        return !!window.AppInterface
    }

    /**
     * 检测是否在 Android App 中
     * @returns {boolean} 是否在 Android App 中
     */
    isAndroidApp(): boolean {
        if (!this.isInApp()) return false
        return typeof window !== 'undefined' &&
            (/Android/i.test(navigator.userAgent) || !!window.AppInterface)
    }

    /**
     * 检测是否在 iOS App 中
     * @returns {boolean} 是否在 iOS App 中
     */
    isIOSApp(): boolean {
        if (!this.isInApp()) return false
        return (
            typeof window !== 'undefined' &&
            /iPhone|iPad|iPod/i.test(navigator.userAgent) &&
            !!window.webkit?.messageHandlers
        )
    }

    /**
     * 调用 App 的 Toast 提示
     * @param {string} message - 提示消息
     */
    async showToast(message: string): Promise<void> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，无法调用 showToast')
            return
        }

        try {
            // 优先尝试使用新通道
            await FlutterBridge.call('flutterMethod', { action: 'showToast', message })
            console.log('[AppBridge] FlutterBridge showToast 调用成功:', message)
            return
        } catch (e) {
            // 降级使用旧通道
        }

        try {
            window.AppInterface?.showToast?.(message)
            console.log('[AppBridge] showToast 调用成功:', message)
        } catch (error) {
            console.error('[AppBridge] showToast 调用失败:', error)
        }
    }

    /**
     * 获取 App 登录信息
     * 
     * App 返回格式：{token: string, uid: string}
     * 
     * 容错机制：
     * - FlutterBridge 失败后自动降级到 AppInterface
     * - 3秒超时自动返回未登录状态
     * - 方法不存在返回未登录状态
     * - 调用异常返回未登录状态并标记错误
     * 
     * @returns {Promise<AppLoginInfo>} App 登录信息
     */
    async getAppLogin(): Promise<AppLoginInfo> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，返回默认未登录状态')
            return { isLogin: false }
        }

        // 优先尝试新通道，根据实际联调的方法名可能为 'getAppLogin' 或 'getUserInfo'
        try {
            const result = await FlutterBridge.call('getAppLogin')
            if (result && typeof result === 'object') {
                const token = result.token || ''
                const uid = result.uid || result.userId || ''
                const isLogin = !!(token && uid)

                if (isLogin) {
                    console.log('[AppBridge] FlutterBridge getAppLogin 成功:', result)
                    return { ...result, isLogin, token, userId: uid, uid }
                }
            }
        } catch (e) {
            console.warn('[AppBridge] FlutterBridge getAppLogin 失败或未使用，降级到旧方法', e)
        }

        try {
            // 检查方法是否存在
            if (!window.AppInterface?.getAppLogin) {
                console.warn('[AppBridge] getAppLogin 方法不存在，回退到 H5 登录流程')
                return {
                    isLogin: false,
                    _error: 'METHOD_NOT_FOUND',
                    _fallbackToH5: true
                }
            }

            // 调用 App 的 getAppLogin 方法
            let result: any = window.AppInterface.getAppLogin()

            // 兼容处理：如果返回的是 Promise，则等待其完成
            if (result instanceof Promise) {
                result = await result
            }

            console.log('[AppBridge] getAppLogin 原始返回:', result)

            // 处理返回结果
            if (result && typeof result === 'object') {
                // App 返回格式：{'token': SettingConfig.token, 'uid': SettingConfig.uid}
                const token = result.token || ''
                const uid = result.uid || ''  // App 只返回 uid 字段

                // 判断是否已登录：token 和 uid 都存在
                const isLogin = !!(token && uid)

                return {
                    ...result,       // 先展开原始数据
                    isLogin,         // 然后用我们的字段覆盖
                    token,
                    userId: uid,     // 将 uid 映射到 userId
                    uid,             // 保留原始 uid 字段
                }
            } else {
                // 未登录或返回数据异常
                console.warn('[AppBridge] App 返回数据格式异常:', result)
                return {
                    isLogin: false,
                    _error: 'INVALID_RESPONSE',
                    _fallbackToH5: true
                }
            }
        } catch (error) {
            console.error('[AppBridge] getAppLogin 调用失败:', error)
            return {
                isLogin: false,
                _error: 'EXCEPTION',
                _fallbackToH5: true,
                _errorDetails: error
            }
        }
    }

    /**
     * 跳转到 App 登录页
     * 
     * 注意：不同 App 可能有不同的登录页跳转方式
     * 这里提供了两种常见方式：
     * 1. 调用 App 的 goToLogin 方法
     * 2. 调用 App 的 navigate 方法并传递路由
     */
    async goToAppLogin(): Promise<void> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，无法跳转到 App 登录页')
            // 在 H5 环境中跳转到 H5 登录页
            if (typeof window !== 'undefined') {
                window.location.href = '/login'
            }
            return
        }

        try {
            await FlutterBridge.call('goLogin')
            console.log('[AppBridge] FlutterBridge goLogin 调用成功')
            return
        } catch (e) {
            // 降级使用旧通道
        }

        try {
            // 方式0: 优先调用新的 goLogin 方法
            if (window.AppInterface?.goLogin) {
                window.AppInterface.goLogin()
                console.log('[AppBridge] goLogin 调用成功')
                return
            }

            // 方式1: 调用原来的 goToLogin 方法
            if (window.AppInterface?.goToLogin) {
                window.AppInterface.goToLogin()
                console.log('[AppBridge] goToLogin 调用成功')
                return
            }

            // 方式2: 调用通用导航方法
            if (window.AppInterface?.navigate) {
                window.AppInterface.navigate('login')
                console.log('[AppBridge] navigate to login 调用成功')
                return
            }

            // 方式3: 使用 postMessage 通知 App
            if (window.webkit?.messageHandlers?.goToLogin) {
                window.webkit.messageHandlers.goToLogin.postMessage({})
                console.log('[AppBridge] iOS postMessage goToLogin 调用成功')
                return
            }

            console.warn('[AppBridge] App 未提供登录跳转方法')
        } catch (error) {
            console.error('[AppBridge] goToAppLogin 调用失败:', error)
        }
    }

    /**
     * 跳转到 App 充值页
     */
    async goToAppRecharge(): Promise<void> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，无法跳转到 App 充值页')
            // H5 充值页
            if (typeof window !== 'undefined') {
                const fromUrl = encodeURIComponent(window.location.href)
                window.location.href = `/user/rechargeDetail?from=${fromUrl}`
            }
            return
        }

        try {
            await FlutterBridge.call('goRecharge')
            console.log('[AppBridge] FlutterBridge goRecharge 调用成功')
            return
        } catch (e) {
            // 降级使用旧通道
        }

        try {
            if (window.AppInterface?.goRecharge) {
                window.AppInterface.goRecharge()
                console.log('[AppBridge] goRecharge 调用成功')
            } else {
                console.warn('[AppBridge] App 未提供 goRecharge 方法')
            }
        } catch (error) {
            console.error('[AppBridge] goToAppRecharge 调用失败:', error)
        }
    }

    /**
     * 关闭当前 WebView（返回 App）
     * 
     * 优先调用 Flutter 统一通道 callFlutter('flutterMethod', { action: 'appBack' })
     * 降级依次尝试 closeWebView → goBack → window.history.back
     */
    async closeWebView(): Promise<void> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，无法关闭 WebView')
            // 在 H5 环境中返回上一页
            if (typeof window !== 'undefined' && window.history.length > 1) {
                window.history.back()
            }
            return
        }

        try {
            // 老版本里用 'appBack' 作为 callFlutter 的参数
            console.log('[AppBridge] FlutterBridge appBack 调用')
            await FlutterBridge.call('flutterMethod', { action: 'appBack' })
            console.log('[AppBridge] FlutterBridge appBack 调用成功')
            return
        } catch (e) {
            // 降级使用旧通道
        }

        try {
            const appInterface = window.AppInterface as any

            // 方式1（优先）：Flutter 统一通道
            if (typeof appInterface?.callFlutter === 'function') {
                appInterface.callFlutter('flutterMethod', { action: 'appBack' })
                console.log('[AppBridge] callFlutter appBack 调用成功')
                return
            }

            // 方式2（降级）：原生 goBack
            if (typeof appInterface?.goBack === 'function') {
                appInterface.goBack()
                console.log('[AppBridge] goBack 调用成功')
                return
            }

            console.warn('[AppBridge] App 未提供任何关闭 WebView 的方法')
        } catch (error) {
            console.error('[AppBridge] closeWebView 调用失败:', error)
        }
    }

    /**
     * 获取 App 状态栏高度（px）
     * 
     * 调用 Flutter 统一通道获取，App 返回 double 类型（如 64.0）
     * 非 App 环境或调用失败时返回 0
     * 
     * @returns {Promise<number>} 状态栏高度（px），如 64
     */
    async getStatusBarHeight(): Promise<number> {
        if (!this.isInApp()) {
            console.log('[AppBridge] 不在 App 环境中，无法获取状态栏高度')
            return 0
        }

        try {
            const result = await FlutterBridge.call('flutterMethod', { action: 'statusBarHeight' })
            if (result !== undefined && result !== null) {
                const height = typeof result === 'number' ? result : parseFloat(result as string)
                if (!isNaN(height) && height > 0) {
                    console.log('[AppBridge] FlutterBridge 获取状态栏高度:', height, 'px')
                    return Math.round(height)
                }
            }
        } catch (e) {
            // 降级去走旧老通道获取（或者直接报错降级）
            console.log('[AppBridge] FlutterBridge getStatusBarHeight 未提供或超时，尝试旧方案')
        }

        try {
            const appInterface = window.AppInterface as any
            if (typeof appInterface?.callFlutter !== 'function') {
                console.warn('[AppBridge] callFlutter 方法不存在，无法获取状态栏高度')
                return 36
            }

            console.log('[AppBridge] 开始调用 Flutter 的 statusBarHeight 方法...')

            // 加入一个3秒超时保护，防止 Flutter 端不返回导致阻塞
            const flutterCall = new Promise<any>((resolve) => {
                // 尝试传入对象。如果 App 要求 JSON 字符串，这里也可改成 JSON.stringify({ action: 'statusBarHeight' })
                // 暂时按照最初始文档传对象
                const req = appInterface.callFlutter('flutterMethod', {
                    action: 'statusBarHeight',
                })
                console.log('[AppBridge] callFlutter 返回值:', req)
                if (req && typeof req.then === 'function') {
                    req.then(resolve).catch((e: any) => {
                        console.error('[AppBridge] callFlutter 内部抛出异常:', e)
                        resolve(36)
                    })
                } else {
                    // 兼容如果不返回 Promise
                    resolve(req)
                }
            })

            const timeout = new Promise<number>((_, reject) => setTimeout(() => reject(new Error('Flutter timeout')), 3000))

            const result = await Promise.race([flutterCall, timeout]).catch((e) => {
                console.error('[AppBridge] 等待 Flutter 返回时发生超时或异常:', e)
                return 36
            })

            console.log('[AppBridge] 获取到 Flutter 返回状态栏高度原始值:', result)

            const height = typeof result === 'number' ? result : parseFloat(result as string)
            if (!isNaN(height) && height > 0) {
                console.log('[AppBridge] 解析后的最终状态栏高度:', height, 'px')
                return Math.round(height)
            }
            return 36
        } catch (error) {
            console.error('[AppBridge] 获取状态栏高度代码出错:', error)
            return 36
        }
    }

    /**
     * 获取 App 渠道信息
     * @returns {Object} 渠道信息
     */
    getChannelInfo() {
        if (!this.isInApp()) {
            return {
                channelID: '',
                channelName: '',
                stationID: '',
                stationName: '',
            }
        }

        try {
            return {
                channelID: window.AppInterface?.getChannelID?.() || '',
                channelName: window.AppInterface?.getChannelName?.() || '',
                stationID: window.AppInterface?.getStationID?.() || '',
                stationName: window.AppInterface?.getStationName?.() || '',
            }
        } catch (error) {
            console.error('[AppBridge] getChannelInfo 调用失败:', error)
            return {
                channelID: '',
                channelName: '',
                stationID: '',
                stationName: '',
            }
        }
    }

    /**
     * 保存图片到相册
     * 
     * @param {string} base64Data - 图片的 Base64 数据 (如 data:image/png;base64,...)
     * @returns {Promise<boolean>} 是否保存成功
     */
    async saveImage(base64Data: string): Promise<boolean> {
        if (!this.isInApp()) {
            console.warn('[AppBridge] 不在 App 环境中，无法调用 saveImage')
            return false
        }

        // 检查当前 App 是否支持 saveImage（v3 新增能力，旧版 App 不支持）
        const supported = await bridgeCapabilities.supports('saveImage')
        if (!supported) {
            console.warn('[AppBridge] 当前 App 版本不支持 saveImage，请提示用户升级')
            return false
        }

        try {
            // 优先调用新通道
            await FlutterBridge.call('saveImage', { base64: base64Data })
            console.log('[AppBridge] FlutterBridge saveImage 调用成功')
            return true
        } catch (e) {
            console.warn('[AppBridge] FlutterBridge saveImage 失败，尝试旧通道')
        }

        try {
            const appInterface = window.AppInterface as any

            if (typeof appInterface?.callFlutter === 'function') {
                appInterface.callFlutter('flutterMethod', {
                    action: 'saveImage',
                    base64: base64Data
                })
                console.log('[AppBridge] callFlutter saveImage 调用成功')
                return true
            } else if (typeof appInterface?.saveImage === 'function') {
                appInterface.saveImage(base64Data)
                console.log('[AppBridge] old saveImage 调用成功')
                return true
            }

            console.warn('[AppBridge] App 未提供任何保存图片的方法')
        } catch (error) {
            console.error('[AppBridge] saveImage 调用失败:', error)
        }
        return false
    }

    /**
     * 调用原生相机或相册选图，并上传到服务器，返回图片 URL
     * - App 环境：通过 uploadImage Bridge 调用 Flutter 原生选图 + 上传
     * - 非 App 环境：触发隐藏的 <input type="file"> 并手动上传
     *
     * @param options.source - 'camera' | 'gallery'，默认 'gallery'
     * @param options.crop - 是否裁剪，默认 false
     * @param options.uploadFn - 非 App 环境的上传回调，入参为 File，需返回上传后的 URL
     * @returns 上传成功的图片 URL，失败时返回空字符串
     */
    async uploadImage(options?: {
        source?: 'camera' | 'gallery'
        crop?: boolean
        flag?: string
        requestId?: string
        uploadFn?: (file: File) => Promise<string>
    }): Promise<string> {
        const { source = 'gallery', crop = false, flag = '1', requestId, uploadFn } = options ?? {}

        if (this.isInApp()) {
            try {
                const supported = await bridgeCapabilities.supports('uploadImage')
                if (!supported) {
                    console.warn('[AppBridge] 当前 App 版本不支持 uploadImage，请提示用户升级')
                    return ''
                }
                // Flutter 端采用 fire-and-forget，立即返回 null，结果统一通过
                // __uploadImageResult 事件下发（同时写 localStorage 防竞态）
                // 彻底解决 iOS WKWebView 页面重载导致 callHandler Promise 永久挂起的问题
                const url = await new Promise<string>((resolveUpload) => {
                    let settled = false
                    const settle = (val: string) => {
                        if (settled) return
                        settled = true
                        window.removeEventListener('__uploadImageResult', handler)
                        document.removeEventListener('visibilitychange', visibilityHandler)
                        resolveUpload(val)
                    }

                    function handler(e: Event) {
                        const detail = (e as CustomEvent).detail
                        console.log('[AppBridge] __uploadImageResult 收到:', JSON.stringify(detail))
                        if (requestId && detail?.requestId && detail.requestId !== requestId) {
                            console.log('[AppBridge] 忽略旧的 __uploadImageResult:', detail.requestId, 'expect:', requestId)
                            return
                        }
                        try {
                            localStorage.removeItem('__pendingUploadUrl')
                            localStorage.removeItem('__pendingUploadResult')
                        } catch {}
                        settle(detail?.url as string || '')
                    }

                    // visibilitychange 兜底：
                    // iOS WKWebView 打开相机时 WebView 进入暂停态，evaluateJavascript
                    // 可能在 WebView 恢复前就执行完毕，事件被错过。
                    // Flutter 端同时写了 localStorage，此处在页面重新可见时读取补救。
                    function visibilityHandler() {
                        if (document.visibilityState !== 'visible') return
                        try {
                            const cached = localStorage.getItem('__pendingUploadResult')
                            if (!cached) return
                            const parsed = JSON.parse(cached) as { url?: string; requestId?: string }
                            if (requestId && parsed?.requestId && parsed.requestId !== requestId) return
                            localStorage.removeItem('__pendingUploadResult')
                            settle(parsed?.url as string || '')
                        } catch {}
                    }

                    window.addEventListener('__uploadImageResult', handler)
                    document.addEventListener('visibilitychange', visibilityHandler)

                    // fire-and-forget：仅用于触发 Flutter 流程，不依赖返回值
                    FlutterBridge.call('uploadImage', { source, crop, flag, requestId }, { _timeout: 5000 }).catch(() => {})
                })
                return url
            } catch (e) {
                console.error('[AppBridge] uploadImage bridge 调用失败:', e)
                return ''
            }
        }

        // 非 App 环境：回退到 <input type="file">
        if (!uploadFn) {
            console.warn('[AppBridge] 非 App 环境需提供 uploadFn')
            return ''
        }
        return new Promise<string>((resolve) => {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/jpeg,image/png,image/gif,image/webp'
            input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;pointer-events:none'
            // 注意：不对 camera source 设置 capture，避免 iOS Safari/WKWebView 页面刷新 bug
            // iOS 的 input[accept="image/*"] 会弹出系统菜单让用户选择"拍照"或"相册"

            let resolved = false
            function done(url: string) {
                if (resolved) return
                resolved = true
                cleanup()
                resolve(url)
            }

            function cleanup() {
                document.removeEventListener('visibilitychange', onVisible)
                if (input.parentNode) input.parentNode.removeChild(input)
            }

            input.onchange = async () => {
                const file = input.files?.[0]
                if (!file) return done('')
                try {
                    const url = await uploadFn(file)
                    done(url)
                } catch (e) {
                    console.error('[AppBridge] uploadFn 失败:', e)
                    done('')
                }
            }

            // 用户取消选择：页面重新可见后等待 2s，若 onchange 未触发则视为取消
            function onVisible() {
                if (document.visibilityState !== 'visible') return
                document.removeEventListener('visibilitychange', onVisible)
                setTimeout(() => { if (!resolved) done('') }, 2000)
            }
            document.addEventListener('visibilitychange', onVisible)

            document.body.appendChild(input)
            input.click()
        })
    }

    /**
     * 调用 App 的通用方法
     * @param {string} methodName - 方法名
     * @param {...any} args - 方法参数
     * @returns {any} 方法返回值
     */
    callAppMethod(methodName: string, ...args: any[]): any {
        if (!this.isInApp()) {
            console.warn(`[AppBridge] 不在 App 环境中，无法调用 ${methodName}`)
            return null
        }

        try {
            const method = (window.AppInterface as any)?.[methodName]
            if (typeof method === 'function') {
                const result = method(...args)
                console.log(`[AppBridge] ${methodName} 调用成功:`, result)
                return result
            } else {
                console.warn(`[AppBridge] App 方法 ${methodName} 不存在`)
                return null
            }
        } catch (error) {
            console.error(`[AppBridge] ${methodName} 调用失败:`, error)
            return null
        }
    }

    /**
     * 跳转到赛事详情页
     * 
     * - App 环境：调用 Flutter 原生导航（callFlutter），参数需 JSON.stringify
     * - H5 环境：使用 Vue Router 跳转到 /detail/:matchId
     * - 降级兜底：callFlutter 抛出异常时自动降级到 H5 路由
     * 
     * @param {string | number} matchId - 赛事 ID
     * @param {string} gameId - 游戏 ID，默认 '202'
     * @param {any} router - Vue Router 实例（H5 环境跳转使用）
     */
    async goToMatchDetail(matchId: string | number, gameId: string = '202', router?: any): Promise<void> {
        if (this.isInApp()) {
            try {
                // 优先新通道
                await FlutterBridge.call('flutterMethod', { action: 'matchDetail', matchId: matchId })
                console.log('[AppBridge] FlutterBridge matchDetail 调用成功:', matchId)
                return
            } catch (e) {
                console.warn('[AppBridge] FlutterBridge matchDetail 不可用，降级使用旧方案')
            }

            try {
                // App 环境：调用 Flutter 原生方法
                const appInterface = window.AppInterface as any
                if (typeof appInterface?.callFlutter === 'function') {
                    appInterface.callFlutter('flutterMethod', {
                        action: 'matchDetail',
                        matchId: matchId,
                    })
                    console.log('[AppBridge] callFlutter matchDetail 调用成功, matchId:', matchId)
                } else {
                    // callFlutter 方法不存在，降级到 H5 路由
                    console.warn('[AppBridge] callFlutter 方法不存在，降级到 H5 路由')
                    router?.push(`/detail/${matchId}?game_id=${gameId}&liveType=`)
                }
            } catch (error) {
                console.error('[AppBridge] callFlutter matchDetail 调用失败，降级到 H5 路由:', error)
                router?.push(`/detail/${matchId}?game_id=${gameId}&liveType=`)
            }
        } else if (hostBridge.getHostType() === 'h5') {
            // H5 iframe 环境：tool_project 没有 /detail 路由
            // 通过 postMessage 通知宿主（jz_h5）进行跳转
            const url = `/detail/${matchId}?game_id=${gameId}&liveType=`
            console.log('[AppBridge] H5 iframe 导航到対应赛事详情页:', url)
            hostBridge.navigateHost(url)
        } else {
            // 浏览器独立访问：直接 Vue Router 跳转
            router?.push(`/detail/${matchId}?game_id=${gameId}&liveType=`)
        }
    }

    /**
     * 跳转到专家详情页
     * 
     * - App 环境：调用 Flutter 原生导航，参数需 JSON.stringify
     * - H5 环境：使用 Vue Router 跳转到 /expert/info/:userId
     * 
     * @param {string | number} userId - 专家 ID
     * @param {any} router - Vue Router 实例
     */
    async goToExpertDetail(userId: string | number, router?: any): Promise<void> {
        if (this.isInApp()) {
            try {
                // 优先新通道
                await FlutterBridge.call('flutterMethod', { action: 'expertDetail', userId: userId })
                console.log('[AppBridge] FlutterBridge expertDetail 调用成功:', userId)
                return
            } catch (e) {
                console.warn('[AppBridge] FlutterBridge expertDetail 不可用，降级使用旧方案')
            }

            try {
                const appInterface = window.AppInterface as any
                if (typeof appInterface?.callFlutter === 'function') {
                    appInterface.callFlutter('flutterMethod', {
                        action: 'expertDetail',
                        userId: userId,
                    })
                    console.log('[AppBridge] callFlutter expertDetail 调用成功, userId:', userId)
                }
            } catch (error) {
                console.error('[AppBridge] callFlutter expertDetail 调用失败:', error)
            }
        } else {
            showToast('请在App内打开')
        }
    }

    /**
     * 跳转到方案详情页
     * 
     * - App 环境：调用 Flutter 原生导航，参数需 JSON.stringify
     * - H5 环境：使用 Vue Router 跳转到 /tactical-board/:planId
     * 
     * @param {string | number} planId - 方案 ID
     * @param {any} router - Vue Router 实例
     */
    async goToPlanDetail(planId: string | number, router?: any): Promise<void> {
        if (this.isInApp()) {
            try {
                // 优先新通道
                await FlutterBridge.call('flutterMethod', { action: 'expertPlanDetail', planId: planId })
                console.log('[AppBridge] FlutterBridge expertPlanDetail 调用成功:', planId)
                return
            } catch (e) {
                console.warn('[AppBridge] FlutterBridge expertPlanDetail 不可用，降级使用旧方案')
            }

            try {
                const appInterface = window.AppInterface as any
                if (typeof appInterface?.callFlutter === 'function') {
                    appInterface.callFlutter('flutterMethod', {
                        action: 'expertPlanDetail',
                        planId: planId,
                    })
                    console.log('[AppBridge] callFlutter expertPlanDetail 调用成功, planId:', planId)
                }
            } catch (error) {
                console.error('[AppBridge] callFlutter expertPlanDetail 调用失败:', error)
            }
        } else {
            showToast('请在App内打开')
        }
    }

    /**
     * 检查 App 登录状态并处理
     * 
     * 这是一个便捷方法，自动检查登录状态并在未登录时跳转
     * 
     * @param {Object} options - 配置项
     * @param {boolean} options.redirectIfNotLogin - 未登录时是否自动跳转到登录页，默认 true
     * @param {Function} options.onLogin - 登录成功的回调
     * @param {Function} options.onNotLogin - 未登录的回调
     * @returns {Promise<AppLoginInfo>} 登录信息
     */
    async checkAndHandleLogin(options: {
        redirectIfNotLogin?: boolean
        onLogin?: (loginInfo: AppLoginInfo) => void
        onNotLogin?: () => void
    } = {}): Promise<AppLoginInfo> {
        const {
            redirectIfNotLogin = true,
            onLogin,
            onNotLogin,
        } = options

        const loginInfo = await this.getAppLogin()

        if (loginInfo.isLogin) {
            console.log('[AppBridge] 用户已登录:', loginInfo)
            onLogin?.(loginInfo)
        } else {
            console.log('[AppBridge] 用户未登录')
            onNotLogin?.()

            if (redirectIfNotLogin) {
                this.goToAppLogin()
            }
        }

        return loginInfo
    }
}

// 导出单例
export const appBridge = new AppBridge()

// 默认导出
export default appBridge
